import React, { createContext, useContext, useEffect, useState } from 'react';

import {
  DateTimeRangeValidationResponse,
  validateDateTimeRange,
} from 'context/DateProvider/utils/validateDateTimeRange';
import { useSearchParamsProvider } from 'context/SearchParamsProvider/SearchParamsProvider';
import { useSnackbar } from 'context/SnackbarProvider/SnackbarProvider';
import { toDates } from 'utils/searchParamsConverter';

export type Dates = {
  from: string;
  to: string;
};

export const NOW = 'now';
export const REGEX = /now(?<operator>[+-])(?<number>[0-9]{1,2})(?<granularity>[mhd])/;

export const defaultDates: Dates = {
  from: 'now-24h',
  to: 'now',
};

const DateContext = createContext<{
  dates: Dates;
  setDates: (dates: Dates) => void;
}>({
  dates: defaultDates,
  setDates: () => {},
});

export const useDates = () => useContext(DateContext);

type DateProviderProps = {
  children: React.ReactNode;
};

export const DateProvider = ({ children }: DateProviderProps) => {
  const { searchParams, updateDatesSearchParams } = useSearchParamsProvider();
  const [dates, setDates] = useState<Dates>(defaultDates);
  const { setSnackbarInfo } = useSnackbar();

  useEffect(() => {
    const parsedDates = toDates(searchParams);
    const validatedDates: DateTimeRangeValidationResponse = validateDateTimeRange(parsedDates);
    if (validatedDates.message) {
      setSnackbarInfo({
        severity: 'error',
        open: true,
        content: {
          header: 'Invalid dates provided',
          message: `${validatedDates.message}. Showing last 24 hours`,
        },
      });
      setDates(defaultDates);
      return;
    }
    if (!validatedDates.newDates.to && !validatedDates.newDates.from) {
      setDates(defaultDates);
    } else {
      setDates(validatedDates.newDates);
    }
  }, [searchParams]);

  const updateDates = (newDates: Dates) => {
    setDates(newDates);
    updateDatesSearchParams(newDates);
  };

  return (
    <DateContext.Provider value={{ dates, setDates: updateDates }}>{children}</DateContext.Provider>
  );
};
